import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  useMediaQuery,
  useTheme,
  Container,
  Autocomplete,
  LinearProgress,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  PersonOff as DeactivateIcon,
  PersonAdd as ActivateIcon,
  Refresh as RefreshIcon,
  LockOpen as LockOpenIcon,
  DeleteForever as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import userManagementApi from "../../services/user-management-api";
import officeLocationsApiService, {
  OfficeLocation,
} from "../../services/office-locations-api";
import { UserWithRoles, UserRole } from "../../types/leave";

interface User extends UserWithRoles {
  // Use the existing UserWithRoles type from leave management
}

interface UserFormData {
  employeeId: string;
  username: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  ldapInitials?: string;
  email: string;
  birthDate?: string;
  homeAddress?: string;
  personalPhone?: string;
  secondaryPhone?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  emergencyContactName2?: string;
  emergencyContactRelationship2?: string;
  emergencyContactPhone2?: string;
  jobTitle: string;
  department: string;
  officeLocation?: string;
  hireDate: string;
  startDate: string;
  roles: UserRole[];
  vacationAllocatedDays: number;
  vacationUsedDays: number;
  vacationCarriedOverDays: number;
}

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [officeLocations, setOfficeLocations] = useState<OfficeLocation[]>([]);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedOfficeLocation, setSelectedOfficeLocation] = useState<
    string | null
  >(null);

  // Server-side pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Delete mode — activated by pressing ESC 7 times
  const [deleteMode, setDeleteMode] = useState(false);
  const [escCount, setEscCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCountdown, setDeleteCountdown] = useState(10);
  const [deleteCountdownActive, setDeleteCountdownActive] = useState(false);

  // Track last ESC press time for double-ESC detection (hide delete mode)
  const lastEscTimeRef = React.useRef<number>(0);

  // ESC key listener — 7 consecutive presses activates delete mode
  // Any other key resets the count back to 0
  // When delete mode is active: double-ESC (2 presses within 400ms) hides the buttons
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (openDialog || confirmDialogOpen || deleteDialogOpen) return;

      if (e.key === "Escape") {
        if (deleteMode) {
          // Double-ESC within 400ms hides delete buttons
          const now = Date.now();
          if (now - lastEscTimeRef.current < 400) {
            setDeleteMode(false);
            setEscCount(0);
            lastEscTimeRef.current = 0;
          } else {
            lastEscTimeRef.current = now;
          }
          return;
        }

        setEscCount((prev) => {
          const next = prev + 1;
          if (next >= 7) {
            setDeleteMode(true);
            return 0;
          }
          return next;
        });
      } else {
        // Any other key breaks the streak
        setEscCount(0);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openDialog, confirmDialogOpen, deleteDialogOpen, deleteMode]);

  // Countdown timer for delete dialog
  useEffect(() => {
    if (!deleteCountdownActive) return;
    if (deleteCountdown <= 0) {
      executDelete();
      return;
    }
    const timer = setTimeout(() => setDeleteCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [deleteCountdownActive, deleteCountdown]);

  const handleDeleteClick = (user: User) => {
    setDeleteTarget(user);
    setDeleteCountdown(10);
    setDeleteCountdownActive(false);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteCountdownActive(true);
  };

  const handleDeleteCancel = () => {
    setDeleteCountdownActive(false);
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
    setDeleteCountdown(10);
  };

  const executDelete = async () => {
    if (!deleteTarget) return;
    setDeleteCountdownActive(false);
    try {
      await userManagementApi.deleteUser(deleteTarget.id);
      setSuccess(`User "${deleteTarget.firstName} ${deleteTarget.lastName}" permanently deleted.`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setDeleteMode(false);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete user");
      setDeleteDialogOpen(false);
    }
  };
  const [formData, setFormData] = useState<UserFormData>({
    employeeId: "",
    username: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    birthDate: "",
    homeAddress: "",
    personalPhone: "",
    secondaryPhone: "",
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",
    emergencyContactName2: "",
    emergencyContactRelationship2: "",
    emergencyContactPhone2: "",
    jobTitle: "",
    department: "",
    hireDate: "",
    startDate: "",
    roles: [UserRole.EMPLOYEE],
    vacationAllocatedDays: 0,
    vacationUsedDays: 0,
    vacationCarriedOverDays: 0,
  });

  const availableRoles: UserRole[] = [
    UserRole.EMPLOYEE,
    UserRole.APPROVER,
    UserRole.ADMIN,
  ];

  useEffect(() => {
    loadOfficeLocations();
  }, []);

  // Reload whenever pagination, sort, or the location filter changes.
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortOrder, selectedOfficeLocation]);

  const loadOfficeLocations = async () => {
    try {
      const locations = await officeLocationsApiService.getAllOfficeLocations();
      setOfficeLocations(locations);
    } catch (err) {
      console.error(t("error.errorLoadingOfficeLocations"), err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const sort = `employeeId,${sortOrder}`;
      const isLocationFiltered =
        !!selectedOfficeLocation && selectedOfficeLocation !== "All";

      if (isLocationFiltered) {
        // The backend has no office-location filter, so fetch the full sorted
        // set and filter/paginate client-side. This keeps totals and page
        // boundaries accurate for the filtered view.
        const data = await userManagementApi.getAllUsers(0, 1000, sort);
        setUsers(data.content);
        setTotalElements(data.totalElements);
      } else {
        // True server-side pagination: only the current page is fetched.
        const data = await userManagementApi.getAllUsers(
          page,
          rowsPerPage,
          sort,
        );
        setUsers(data.content);
        setTotalElements(data.totalElements);
      }
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadUsers"),
      );
    } finally {
      setLoading(false);
    }
  };

  const isLocationFiltered =
    !!selectedOfficeLocation && selectedOfficeLocation !== "All";

  // Rows to render. The list is already sorted by the backend (employeeId).
  // - No location filter: `users` is the current server page, render as-is.
  // - Location filter active: `users` holds the full set, so filter here and
  //   slice to the current client-side page.
  const displayedUsers = useMemo(() => {
    if (!isLocationFiltered) {
      return users;
    }
    const filtered = users.filter(
      (user) => user.officeLocation === selectedOfficeLocation,
    );
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [
    users,
    isLocationFiltered,
    selectedOfficeLocation,
    page,
    rowsPerPage,
  ]);

  // Total row count driving the pagination control.
  const paginationCount = useMemo(() => {
    if (!isLocationFiltered) {
      return totalElements;
    }
    return users.filter(
      (user) => user.officeLocation === selectedOfficeLocation,
    ).length;
  }, [isLocationFiltered, totalElements, users, selectedOfficeLocation]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddUser = () => {
    setDialogMode("add");
    setSelectedUser(null);
    setFormData({
      employeeId: "",
      username: "",
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      birthDate: "",
      homeAddress: "",
      personalPhone: "",
      secondaryPhone: "",
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactPhone: "",
      emergencyContactName2: "",
      emergencyContactRelationship2: "",
      emergencyContactPhone2: "",
      jobTitle: "",
      department: "",
      officeLocation: "",
      hireDate: new Date().toISOString().split("T")[0], // Default to today
      startDate: new Date().toISOString().split("T")[0], // Default to today
      roles: [UserRole.EMPLOYEE],
      vacationAllocatedDays: 20, // Default Vacation Leave allocation for current year
      vacationUsedDays: 0, // Default no days used yet
      vacationCarriedOverDays: 0, // Default no carried over days
    });
    setOpenDialog(true);
  };

  const handleEditUser = (user: User) => {
    setDialogMode("edit");
    setSelectedUser(user);

    // Helper function to convert date array or string to ISO format
    const formatDateForInput = (date: any): string => {
      if (!date) return "";

      // If it's an array [year, month, day]
      if (Array.isArray(date)) {
        const [year, month, day] = date;
        // Pad month and day with zeros if needed
        const monthStr = String(month).padStart(2, "0");
        const dayStr = String(day).padStart(2, "0");
        return `${year}-${monthStr}-${dayStr}`;
      }

      // If it's already a string, return as is
      if (typeof date === "string") {
        return date;
      }

      return "";
    };

    setFormData({
      employeeId: user.employeeId,
      username: user.username,
      firstName: user.firstName,
      middleName: user.middleName || "",
      lastName: user.lastName,
      ldapInitials: user.ldapInitials || "",
      email: user.email,
      birthDate: formatDateForInput(user.birthDate),
      homeAddress: user.homeAddress || "",
      personalPhone: user.personalPhone || "",
      secondaryPhone: user.secondaryPhone || "",
      emergencyContactName: user.emergencyContactName || "",
      emergencyContactRelationship: user.emergencyContactRelationship || "",
      emergencyContactPhone: user.emergencyContactPhone || "",
      emergencyContactName2: user.emergencyContactName2 || "",
      emergencyContactRelationship2: user.emergencyContactRelationship2 || "",
      emergencyContactPhone2: user.emergencyContactPhone2 || "",
      jobTitle: user.jobTitle || "",
      department: user.department || "",
      officeLocation: user.officeLocation || "",
      hireDate: formatDateForInput(user.hireDate),
      startDate: formatDateForInput(user.startDate),
      roles: user.roles,
      vacationAllocatedDays: 0, // Not used for edit mode
      vacationUsedDays: 0, // Not used for edit mode
      vacationCarriedOverDays: 0, // Not used for edit mode
    });
    setOpenDialog(true);
  };

  const handleActivateUser = async (userId: number) => {
    try {
      await userManagementApi.activateUser(userId);
      setSuccess(t("success.userActivatedSuccessfully"));
      loadUsers();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToActivateUser"),
      );
    }
  };

  const handleDeactivateUser = async (userId: number) => {
    try {
      await userManagementApi.deactivateUser(userId);
      setSuccess("User deactivated successfully");
      loadUsers();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to deactivate user",
      );
    }
  };

  const handleUnlockUser = async (userId: number) => {
    try {
      await userManagementApi.unlockUser(userId);
      setSuccess("User account unlocked successfully");
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unlock user");
    }
  };

  const validateForm = async (): Promise<boolean> => {
    if (dialogMode === "add") {
      // Check for duplicate username
      const existingUserByUsername = users.find(
        (u) => u.username.toLowerCase() === formData.username.toLowerCase(),
      );
      if (existingUserByUsername) {
        setError(
          "Username already exists. Please choose a different username.",
        );
        return false;
      }

      // Check for duplicate employee ID
      const existingUserByEmployeeId = users.find(
        (u) => u.employeeId === formData.employeeId,
      );
      if (existingUserByEmployeeId) {
        setError(
          "Employee ID already exists. Please choose a different employee ID.",
        );
        return false;
      }

      // Validate Vacation Leave balance logic
      const totalAvailable =
        formData.vacationAllocatedDays + formData.vacationCarriedOverDays;
      if (formData.vacationUsedDays > totalAvailable) {
        setError(
          `Used days (${formData.vacationUsedDays}) cannot exceed total available days (${totalAvailable} = ${formData.vacationAllocatedDays} allocated + ${formData.vacationCarriedOverDays} carried over).`,
        );
        return false;
      }

      // Validate reasonable ranges
      if (
        formData.vacationAllocatedDays < 0 ||
        formData.vacationAllocatedDays > 365
      ) {
        setError("Allocated days must be between 0 and 365.");
        return false;
      }

      if (formData.vacationUsedDays < 0) {
        setError("Used days cannot be negative.");
        return false;
      }

      if (
        formData.vacationCarriedOverDays < 0 ||
        formData.vacationCarriedOverDays > 50
      ) {
        setError("Carried over days must be between 0 and 50.");
        return false;
      }
    }
    return true;
  };

  const handleSubmitForm = async () => {
    try {
      // Validate form
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      if (dialogMode === "add") {
        // Show confirmation dialog instead of submitting directly
        setConfirmDialogOpen(true);
        return;
      }

      // Edit mode — submit directly (no confirmation needed)
      await submitForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save user");
    }
  };

  const submitForm = async () => {
    try {
      if (dialogMode === "add") {
        await userManagementApi.createUser(formData);
        setSuccess("User created successfully");
      } else if (selectedUser) {
        // Update profile with all required fields
        await userManagementApi.updateUserProfile(selectedUser.id, {
          id: selectedUser.id,
          employeeId: formData.employeeId,
          username: formData.username,
          firstName: formData.firstName,
          middleName: formData.middleName,
          lastName: formData.lastName,
          ldapInitials: formData.ldapInitials,
          email: formData.email,
          birthDate: formData.birthDate,
          homeAddress: formData.homeAddress,
          personalPhone: formData.personalPhone,
          secondaryPhone: formData.secondaryPhone,
          emergencyContactName: formData.emergencyContactName,
          emergencyContactRelationship: formData.emergencyContactRelationship,
          emergencyContactPhone: formData.emergencyContactPhone,
          emergencyContactName2: formData.emergencyContactName2,
          emergencyContactRelationship2: formData.emergencyContactRelationship2,
          emergencyContactPhone2: formData.emergencyContactPhone2,
          jobTitle: formData.jobTitle,
          department: formData.department,
          officeLocation: formData.officeLocation,
          hireDate: formData.hireDate,
          startDate: formData.startDate,
          status: selectedUser.status,
          roles: formData.roles,
          version: selectedUser.version || 0,
        });
        setSuccess("User updated successfully");
      }
      setConfirmDialogOpen(false);
      setOpenDialog(false);
      loadUsers();
    } catch (err) {
      setConfirmDialogOpen(false);
      setError(err instanceof Error ? err.message : "Failed to save user");
    }
  };

  const handleFormChange = useCallback(
    (field: keyof UserFormData, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return "error";
      case UserRole.APPROVER:
        return "warning";
      case UserRole.EMPLOYEE:
        return "primary";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  // Helper function to parse LocalDateTime array and check if locked
  const isUserLocked = (lockedUntil: any): boolean => {
    if (!lockedUntil) return false;

    let lockDate: Date;

    // Handle array format from Java LocalDateTime serialization
    if (Array.isArray(lockedUntil)) {
      const [year, month, day, hour, minute, second] = lockedUntil;
      lockDate = new Date(year, month - 1, day, hour, minute, second);
    } else if (typeof lockedUntil === "string") {
      lockDate = new Date(lockedUntil);
    } else if (typeof lockedUntil === "number") {
      lockDate = new Date(lockedUntil * 1000);
    } else {
      return false;
    }

    return lockDate > new Date();
  };

  // Helper function to get remaining lock time in minutes
  const getRemainingLockMinutes = (lockedUntil: any): number => {
    if (!lockedUntil) return 0;

    let lockDate: Date;

    // Handle array format from Java LocalDateTime serialization
    if (Array.isArray(lockedUntil)) {
      const [year, month, day, hour, minute, second] = lockedUntil;
      lockDate = new Date(year, month - 1, day, hour, minute, second);
    } else if (typeof lockedUntil === "string") {
      lockDate = new Date(lockedUntil);
    } else {
      return 0;
    }

    const now = new Date();
    const diffMs = lockDate.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60));
  };

  // Helper function to format lock duration for display
  const formatLockDuration = (minutes: number): string => {
    if (minutes <= 0) return "Unlocked";
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""}`;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (mins === 0) {
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    }
    return `${hours}h ${mins}m`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">{t("users.title")}</Typography>
        <Box display="flex" gap={1}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t("users.officeLocation")}</InputLabel>
            <Select
              value={selectedOfficeLocation || ""}
              label={t("users.officeLocation")}
              onChange={(e) => {
                setSelectedOfficeLocation(e.target.value || null);
                setPage(0);
              }}
            >
              <MenuItem value="All">{t("common.all")}</MenuItem>
              {officeLocations.map((location) => (
                <MenuItem key={location.id} value={location.name}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loading}
          >
            {t("common.refresh")}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
          >
            {t("users.addUser")}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, width: "100%", overflowX: "auto" }}>
        <TableContainer>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortOrder}>
                  <TableSortLabel
                    active
                    direction={sortOrder}
                    onClick={() => {
                      setSortOrder((prev) =>
                        prev === "asc" ? "desc" : "asc",
                      );
                      setPage(0);
                    }}
                  >
                    {t("users.employeeId")}
                  </TableSortLabel>
                </TableCell>
                {/* <TableCell>{t("users.username")}</TableCell> */}
                <TableCell>{t("users.name")}</TableCell>
                {!isMobile && <TableCell>{t("users.email")}</TableCell>}
                {!isMobile && <TableCell>{t("users.department")}</TableCell>}
                {!isMobile && <TableCell>{t("users.jobTitle")}</TableCell>}
                {!isMobile && (
                  <TableCell>{t("users.officeLocation")}</TableCell>
                )}
                <TableCell>{t("users.roles")}</TableCell>
                <TableCell>{t("users.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {user.employeeId}
                    </Typography>
                  </TableCell>
                  {/* <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {user.username}
                    </Typography>
                  </TableCell> */}
                  <TableCell>
                    <Typography variant="body2">
                      {user.firstName} {user.lastName}
                    </Typography>
                    {isMobile && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {user.email}
                      </Typography>
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">{user.department}</Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">{user.jobTitle}</Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">
                        {user.officeLocation || "-"}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {user.roles.map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          size="small"
                          color={getRoleColor(role) as any}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title={t("users.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {user.status === "ACTIVE" ? (
                        <Tooltip title={t("users.deactivate")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeactivateUser(user.id)}
                          >
                            <DeactivateIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t("users.activate")}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleActivateUser(user.id)}
                          >
                            <ActivateIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isUserLocked(user.lockedUntil) && (
                        <Tooltip
                          title={`Unlock Account - Locked for ${formatLockDuration(getRemainingLockMinutes(user.lockedUntil))}`}
                        >
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleUnlockUser(user.id)}
                          >
                            <LockOpenIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {deleteMode && (
                        <Tooltip title="Permanently Delete User">
                          <IconButton
                            size="small"
                            sx={{ color: "error.dark" }}
                            onClick={() => handleDeleteClick(user)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {displayedUsers.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">
                {t("common.noData")}
              </Typography>
            </Box>
          )}
        </TableContainer>
        <TablePagination
          component="div"
          count={paginationCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.5rem",
            fontWeight: 600,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {dialogMode === "add" ? t("users.addUser") : t("users.editUser")}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Account Information Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                {t("users.accountInfo")}
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                <TextField
                  label={t("users.employeeId")}
                  value={formData.employeeId}
                  onChange={(e) =>
                    handleFormChange("employeeId", e.target.value)
                  }
                  fullWidth
                  required
                  disabled={dialogMode === "edit"}
                  size="small"
                />
                <TextField
                  label={t("users.username")}
                  value={formData.username}
                  onChange={(e) => handleFormChange("username", e.target.value)}
                  fullWidth
                  required
                  disabled={dialogMode === "edit"}
                  size="small"
                />
              </Box>
            </Box>

            {/* Personal Information Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                {t("users.personalInfo")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 0.8fr 1fr"
                  gap={1.5}
                >
                  <TextField
                    label={t("users.firstName")}
                    value={formData.firstName}
                    onChange={(e) =>
                      handleFormChange("firstName", e.target.value)
                    }
                    fullWidth
                    required
                    size="small"
                  />
                  <TextField
                    label={t("users.middleName")}
                    value={formData.middleName || ""}
                    onChange={(e) =>
                      handleFormChange("middleName", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("users.lastName")}
                    value={formData.lastName}
                    onChange={(e) =>
                      handleFormChange("lastName", e.target.value)
                    }
                    fullWidth
                    required
                    size="small"
                  />
                </Box>
                <TextField
                  label={t("users.ldapInitials")}
                  value={formData.ldapInitials || ""}
                  onChange={(e) =>
                    handleFormChange("ldapInitials", e.target.value)
                  }
                  fullWidth
                  size="small"
                  helperText={t("users.ldapInitialsHelperText")}
                />
                <TextField
                  label={t("users.email")}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  fullWidth
                  required
                  size="small"
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                  <TextField
                    label={t("users.birthDate")}
                    type="date"
                    value={formData.birthDate || ""}
                    onChange={(e) =>
                      handleFormChange("birthDate", e.target.value)
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label={t("users.homeAddress")}
                    value={formData.homeAddress || ""}
                    onChange={(e) =>
                      handleFormChange("homeAddress", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>
            </Box>

            {/* Job Information Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                {t("users.jobInfo")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                  <TextField
                    label={t("users.department")}
                    value={formData.department}
                    onChange={(e) =>
                      handleFormChange("department", e.target.value)
                    }
                    fullWidth
                    required
                    size="small"
                  />
                  <TextField
                    label={t("users.jobTitle")}
                    value={formData.jobTitle}
                    onChange={(e) =>
                      handleFormChange("jobTitle", e.target.value)
                    }
                    fullWidth
                    required
                    size="small"
                  />
                </Box>
                <Autocomplete
                  options={officeLocations}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name
                  }
                  value={
                    officeLocations.find(
                      (loc) => loc.name === formData.officeLocation,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleFormChange(
                      "officeLocation",
                      newValue ? newValue.name : "",
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("users.officeLocation")}
                      size="small"
                    />
                  )}
                  fullWidth
                  freeSolo
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                  <TextField
                    label={t("users.hireDate")}
                    type="date"
                    value={formData.hireDate}
                    onChange={(e) =>
                      handleFormChange("hireDate", e.target.value)
                    }
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label={t("users.startDate")}
                    type="date"
                    value={formData.startDate}
                    onChange={(e) =>
                      handleFormChange("startDate", e.target.value)
                    }
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>

            {/* Contact Information Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                {t("users.contactInfo")}
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                <TextField
                  label={t("users.personalPhone")}
                  type="tel"
                  value={formData.personalPhone || ""}
                  onChange={(e) =>
                    handleFormChange("personalPhone", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("users.secondaryPhone")}
                  type="tel"
                  value={formData.secondaryPhone || ""}
                  onChange={(e) =>
                    handleFormChange("secondaryPhone", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Box>
            </Box>

            {/* Emergency Contact 1 Section */}
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                {t("users.emergencyContact1")}
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                <TextField
                  label={t("users.emergencyContactName")}
                  value={formData.emergencyContactName || ""}
                  onChange={(e) =>
                    handleFormChange("emergencyContactName", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("users.emergencyContactRelationship")}
                  value={formData.emergencyContactRelationship || ""}
                  onChange={(e) =>
                    handleFormChange(
                      "emergencyContactRelationship",
                      e.target.value,
                    )
                  }
                  fullWidth
                  size="small"
                />
              </Box>
              <TextField
                label={t("users.emergencyContactPhone")}
                type="tel"
                value={formData.emergencyContactPhone || ""}
                onChange={(e) =>
                  handleFormChange("emergencyContactPhone", e.target.value)
                }
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
              />
            </Box>

            {/* Emergency Contact 2 Section */}
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                {t("users.emergencyContact2")}
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                <TextField
                  label={t("users.emergencyContactName2")}
                  value={formData.emergencyContactName2 || ""}
                  onChange={(e) =>
                    handleFormChange("emergencyContactName2", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("users.emergencyContactRelationship2")}
                  value={formData.emergencyContactRelationship2 || ""}
                  onChange={(e) =>
                    handleFormChange(
                      "emergencyContactRelationship2",
                      e.target.value,
                    )
                  }
                  fullWidth
                  size="small"
                />
              </Box>
              <TextField
                label={t("users.emergencyContactPhone2")}
                type="tel"
                value={formData.emergencyContactPhone2 || ""}
                onChange={(e) =>
                  handleFormChange("emergencyContactPhone2", e.target.value)
                }
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
              />
            </Box>

            {/* Roles Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                {t("users.roles")}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t("users.roles")}</InputLabel>
                <Select
                  multiple
                  value={formData.roles}
                  onChange={(e) => handleFormChange("roles", e.target.value)}
                  label={t("users.roles")}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {dialogMode === "add" && (
              <>
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                      mb: 1,
                    }}
                  >
                    {t("users.vacationLeaveBalance")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1.5, display: "block" }}
                  >
                    {t("users.vacationLeaveBalanceHelp")}
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                    }}
                  >
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                      <TextField
                        label={t("users.vacationAllocatedDays")}
                        type="number"
                        value={formData.vacationAllocatedDays}
                        onChange={(e) =>
                          handleFormChange(
                            "vacationAllocatedDays",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        fullWidth
                        required
                        inputProps={{ min: 0, max: 365, step: 0.5 }}
                        size="small"
                      />
                      <TextField
                        label={t("users.vacationUsedDays")}
                        type="number"
                        value={formData.vacationUsedDays}
                        onChange={(e) =>
                          handleFormChange(
                            "vacationUsedDays",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        fullWidth
                        inputProps={{
                          min: 0,
                          max:
                            formData.vacationAllocatedDays +
                            formData.vacationCarriedOverDays,
                          step: 0.5,
                        }}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                    }}
                  >
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                      <TextField
                        label={t("users.vacationCarriedOverDays")}
                        type="number"
                        value={formData.vacationCarriedOverDays}
                        onChange={(e) =>
                          handleFormChange(
                            "vacationCarriedOverDays",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        fullWidth
                        inputProps={{ min: 0, max: 50, step: 0.5 }}
                        size="small"
                      />
                      <TextField
                        label={t("users.vacationAvailableDays")}
                        type="number"
                        value={
                          formData.vacationAllocatedDays +
                          formData.vacationCarriedOverDays -
                          formData.vacationUsedDays
                        }
                        InputProps={{ readOnly: true }}
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "rgba(0, 0, 0, 0.06)",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmitForm}
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            {dialogMode === "add" ? t("users.create") : t("users.update")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={deleteCountdownActive ? undefined : handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: "2px solid", borderColor: "error.main" } }}
      >
        <DialogTitle sx={{ fontSize: "1.1rem", fontWeight: 700, color: "error.main", pb: 1, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteIcon fontSize="small" />
          Permanently Delete User
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>This action is irreversible.</strong> The user and all associated data will be permanently removed.
          </Alert>
          {deleteTarget && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>User to be deleted:</Typography>
              <Typography variant="body1" fontWeight={700}>{deleteTarget.firstName} {deleteTarget.lastName}</Typography>
              <Typography variant="body2" color="text.secondary">{deleteTarget.employeeId} · {deleteTarget.username}</Typography>
              <Typography variant="body2" color="text.secondary">{deleteTarget.email}</Typography>
            </Box>
          )}
          {deleteCountdownActive ? (
            <Box sx={{ pt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={((10 - deleteCountdown) / 10) * 100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mb: 1.5,
                  backgroundColor: "#ffcdd2",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#c62828",
                    transition: "transform 1s linear",
                    borderRadius: 3,
                  },
                }}
              />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Deletion will proceed in a moment — click <strong>Cancel</strong> to stop.
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          {!deleteCountdownActive && (
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Delete Permanently
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create User Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          Confirm New User Creation
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Please review the details below before creating this user.
          </Alert>

          {/* Account Information */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px", mb: 1 }}>
            Account Information
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Employee ID</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.employeeId || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Username</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.username || "—"}</Typography>
            </Box>
          </Box>

          {/* Personal Information */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px", mb: 1 }}>
            Personal Information
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Full Name</Typography>
              <Typography variant="body2" fontWeight={500}>
                {[formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(" ") || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.email || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Birth Date</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.birthDate || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">LDAP Initials</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.ldapInitials || "—"}</Typography>
            </Box>
            {formData.homeAddress && (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="caption" color="text.secondary">Home Address</Typography>
                <Typography variant="body2" fontWeight={500}>{formData.homeAddress}</Typography>
              </Box>
            )}
          </Box>

          {/* Job Information */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px", mb: 1 }}>
            Job Information
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Department</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.department || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Job Title</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.jobTitle || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Office Location</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.officeLocation || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Roles</Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.25}>
                {formData.roles.map((role) => (
                  <Chip key={role} label={role} size="small" color={getRoleColor(role) as any} />
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Hire Date</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.hireDate || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Start Date</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.startDate || "—"}</Typography>
            </Box>
          </Box>

          {/* Contact Information */}
          {(formData.personalPhone || formData.secondaryPhone) && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px", mb: 1 }}>
                Contact Information
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: 2 }}>
                {formData.personalPhone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Personal Phone</Typography>
                    <Typography variant="body2" fontWeight={500}>{formData.personalPhone}</Typography>
                  </Box>
                )}
                {formData.secondaryPhone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Secondary Phone</Typography>
                    <Typography variant="body2" fontWeight={500}>{formData.secondaryPhone}</Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Emergency Contacts */}
          {formData.emergencyContactName && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px", mb: 1 }}>
                Emergency Contact 1
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" fontWeight={500}>{formData.emergencyContactName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Relationship</Typography>
                  <Typography variant="body2" fontWeight={500}>{formData.emergencyContactRelationship || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" fontWeight={500}>{formData.emergencyContactPhone || "—"}</Typography>
                </Box>
              </Box>
            </>
          )}
          {formData.emergencyContactName2 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px", mb: 1 }}>
                Emergency Contact 2
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" fontWeight={500}>{formData.emergencyContactName2}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Relationship</Typography>
                  <Typography variant="body2" fontWeight={500}>{formData.emergencyContactRelationship2 || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" fontWeight={500}>{formData.emergencyContactPhone2 || "—"}</Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Vacation Leave Balance */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px", mb: 1 }}>
            Vacation Leave Balance
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr" gap={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">Allocated</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.vacationAllocatedDays} days</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Used</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.vacationUsedDays} days</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Carried Over</Typography>
              <Typography variant="body2" fontWeight={500}>{formData.vacationCarriedOverDays} days</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Available</Typography>
              <Typography variant="body2" fontWeight={600} color="success.main">
                {formData.vacationAllocatedDays + formData.vacationCarriedOverDays - formData.vacationUsedDays} days
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Back to Edit
          </Button>
          <Button
            onClick={submitForm}
            variant="contained"
            color="primary"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Confirm & Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;
